import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useUserContext } from 'providers';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverTrigger, PopoverContent } from 'components/ui/popover';
import { Sheet, SheetTrigger, SheetContent } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { ChevronLeft, ChevronRight, LogOut, User } from 'lucide-react';
import { ProfileAvatar } from 'pages/Profile/components';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 767px)').matches);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return isMobile;
}

interface Props {
  setProfileDetailsVisible?: (value: boolean) => void;
  triggerButton?: React.ReactNode;
}

const ProfilesCard: FC<Props> = memo(
  ({ setProfileDetailsVisible, triggerButton }) => {
    const {
      user,
      company,
      logout,
      setCompany,
      currentProfile,
      setCurrentProfile,
      showSignInModal,
    } = useUserContext();
    const navigate = useNavigate();

    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const isMobile = useIsMobile();

    const isInitializedRef = useRef(false);
    const lastProfileRef = useRef<string | null>(null);

    useEffect(() => {
      if (!user || !currentProfile || isInitializedRef.current) return;

      let initialIndex = 0;

      if (currentProfile.currentType === 'user') {
        initialIndex = 0;
      } else if (
        currentProfile.currentType === 'company' &&
        company &&
        user.companies?.length
      ) {
        const companyIndex = user.companies.findIndex(
          (c) => c.id === company.id
        );
        initialIndex = companyIndex >= 0 ? companyIndex + 1 : 0;
      }

      setCurrentIndex(initialIndex);
      isInitializedRef.current = true;
      lastProfileRef.current =
        currentProfile.currentType === 'user'
          ? 'user'
          : `company-${company?.id}`;
    }, [user, currentProfile, company]);

    const handleProfileChange = useCallback(
      (newIndex: number) => {
        if (!user || newIndex === currentIndex) return;

        const companies = user.companies ?? [];
        const newProfileKey =
          newIndex === 0 ? 'user' : `company-${companies[newIndex - 1]?.id}`;

        if (lastProfileRef.current === newProfileKey) return;

        setCurrentIndex(newIndex);
        lastProfileRef.current = newProfileKey;

        if (newIndex === 0) {
          setCurrentProfile({ ...user, currentType: 'user' });
        } else {
          const selectedCompany = companies[newIndex - 1];
          if (selectedCompany) {
            setCompany(selectedCompany);
            setCurrentProfile({ ...selectedCompany, currentType: 'company' });
          }
        }
      },
      [currentIndex, user, setCompany, setCurrentProfile]
    );

    const onClickPrev = useCallback(() => {
      if (currentIndex !== null && currentIndex > 0) {
        handleProfileChange(currentIndex - 1);
      }
    }, [currentIndex, handleProfileChange]);

    const onClickNext = useCallback(() => {
      if (
        currentIndex !== null &&
        currentIndex < (user.companies ?? []).length
      ) {
        handleProfileChange(currentIndex + 1);
      }
    }, [currentIndex, user.companies, handleProfileChange]);

    const onClickLogout = useCallback(() => {
      logout();
      setIsOpen(false);
    }, [logout]);

    const onClickProfile = useCallback(() => {
      navigate(currentIndex === 0 ? '/profile' : '/profile/company');
      setProfileDetailsVisible?.(false);
      setIsOpen(false);
    }, [currentIndex, navigate, setProfileDetailsVisible]);

    if (!user || !user.authenticated) {
      const loginContent = (
        <div className="w-80 max-w-full p-4 flex flex-col space-y-3">
          <Button
            variant="default"
            className="w-full justify-start"
            onClick={() => {
              showSignInModal(true);
              setIsOpen(false);
            }}
          >
            <User className="mr-2 h-4 w-4" />
            Войти
          </Button>
        </div>
      );

      const defaultTrigger = (
        <Button variant="ghost" size="icon" className="rounded-full">
          <ProfileAvatar
            className="w-8 h-8 rounded-full"
            size={32}
            avatarFiles={''}
          />
        </Button>
      );

      if (isMobile) {
        return (
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              {triggerButton ?? defaultTrigger}
            </SheetTrigger>
            <SheetContent side="right" className="p-0 w-full max-w-xs">
              {loginContent}
            </SheetContent>
          </Sheet>
        );
      }

      return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            {triggerButton ?? defaultTrigger}
          </PopoverTrigger>
          <PopoverContent align="end" className="p-0 w-80">
            {loginContent}
          </PopoverContent>
        </Popover>
      );
    }

    if (currentIndex === null || !currentProfile) {
      const defaultTrigger = (
        <Button variant="ghost" size="icon" className="rounded-full">
          <ProfileAvatar
            className="w-8 h-8 rounded-full"
            size={32}
            avatarFiles={''}
          />
        </Button>
      );

      return triggerButton ? <>{triggerButton}</> : defaultTrigger;
    }

    const companies = user.companies ?? [];
    const profileName =
      currentIndex === 0
        ? `${user.firstName} ${user.lastName}`
        : companies[currentIndex - 1]?.name || 'Компания';

    const profileType = currentIndex === 0 ? 'Личный профиль' : 'Сотрудник';

    const avatarFiles =
      currentIndex === 0
        ? currentProfile.avatarFilesIds?.split(',')[0] || ''
        : companies[currentIndex - 1]?.avatarFilesIds?.split(',')[0] || '';

    const renderDots = () => (
      <div className="flex justify-center space-x-1 mb-3">
        <div
          className={`w-2 h-2 rounded-full ${
            currentIndex === 0 ? 'bg-blue-600' : 'bg-gray-300'
          }`}
        />
        {companies.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full ${
              currentIndex === i + 1 ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    );

    const content = (
      <div className="w-80 max-w-full p-4 flex flex-col space-y-4">
        {companies.length > 0 && renderDots()}
        <div className="flex items-center space-x-3">
          <ProfileAvatar
            className="w-12 h-12 rounded-full"
            size={48}
            avatarFiles={avatarFiles}
          />
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{profileName}</div>
            <div className="text-sm text-muted-foreground truncate">
              {profileType}
            </div>
          </div>
          {companies.length > 0 && (
            <div className="flex space-x-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClickPrev}
                disabled={currentIndex === 0}
                className="h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClickNext}
                disabled={currentIndex === companies.length}
                className="h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <Separator />
        <Button
          variant="outline"
          className="w-full justify-start profile-dropdown-link action-link-fade"
          onClick={onClickProfile}
        >
          <User className="mr-2 h-4 w-4" />
          Перейти в профиль
        </Button>
        <Separator />
        <Button
          variant="destructive"
          className="w-full justify-start"
          onClick={onClickLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Выйти
        </Button>
      </div>
    );

    const defaultTrigger = (
      <Button variant="ghost" size="icon" className="rounded-full">
        <ProfileAvatar
          className="w-8 h-8 rounded-full"
          size={32}
          avatarFiles={avatarFiles}
        />
      </Button>
    );

    if (isMobile) {
      return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>{triggerButton ?? defaultTrigger}</SheetTrigger>
          <SheetContent side="right" className="p-0 w-full max-w-xs">
            {content}
          </SheetContent>
        </Sheet>
      );
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {triggerButton ?? defaultTrigger}
        </PopoverTrigger>
        <PopoverContent align="end" className="p-0 w-80">
          {content}
        </PopoverContent>
      </Popover>
    );
  }
);

export default ProfilesCard;
